import { Button } from "@sellar-io/components";
import { useState, useMemo } from "react";
import {
  InputLabel,
  InputContainer,
  InputOption,
  InputSelect,
} from "@sellar-io/components";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../components/modal/Dialog";
import { apiV2AssertError } from "../../../hooks/api/v2/apiV2Request";
import { usePriceListList } from "../../../hooks/api/v2/priceList/usePriceListList";
import { usePriceListVolumeDiscountsList } from "../../../hooks/api/v2/priceListVolumeDiscounts/usePriceListVolumeDiscountsList";
import { useVolumeDiscountPriceTierCreate } from "../../../hooks/api/v2/volumeDiscounts/useVolumeDiscountPriceTierCreate";
import { SpinnerLegacy } from "../../../components";
import { capitalize } from "@sellar-io/common";
import {
  PriceListVolumeDiscountsListResponseType,
  AllFulfillmentTypesType,
} from "@sellar-io/api-v2/types";
import { useRemoteFeatureFlags } from "../../../hooks/useRemoteFeatureFlags";
import { useVolumeDiscountMode } from "../../../hooks/useVolumeDiscountMode";
import { ConditionalVolumeDiscountsPrompt } from "./VolumeDiscountModeDialogs";

type FulfillmentType = Extract<
  AllFulfillmentTypesType,
  "direct" | "courier" | "haulier" | "collection" | "sellarDistribution"
> | null;

export const VolumeDiscountPriceListAssignDialog = ({
  active,
  setActive,
  volumeDiscountId,
  onSetUpConditionalVolumeDiscounts,
}: {
  active: boolean;
  setActive: (active: boolean) => void;
  volumeDiscountId: number;
  onSetUpConditionalVolumeDiscounts: () => void;
}) => {
  const [error, setError] = useState("");
  const [selectedPriceListId, setSelectedPriceListId] = useState<number | null>(
    null,
  );
  const [selectedFulfillmentTypes, setSelectedFulfillmentTypes] = useState<
    Set<NonNullable<FulfillmentType>>
  >(new Set());
  const [isDefault, setIsDefault] = useState(false);

  const priceListListQuery = usePriceListList(
    {
      sortBy: "name",
      sortDir: "asc",
      limit: 100,
      query: [
        {
          type: "BasePriceListId",
          modifier: "NOT_NULL",
        },
      ],
    },
    { enabled: active },
  );

  const priceLists = useMemo(
    () => priceListListQuery.data?.pages.flat() ?? [],
    [priceListListQuery.data],
  );

  const assignMutation = useVolumeDiscountPriceTierCreate(volumeDiscountId);
  const volumeDiscountMode = useVolumeDiscountMode();
  const isBasicMode = volumeDiscountMode.isBasicMode;

  const sellarDistributionEnabled = useRemoteFeatureFlags().isActive(
    "sellarDistributionBeta",
  );

  const fulfillmentTypes: Array<{
    value: NonNullable<FulfillmentType>;
    label: string;
  }> = [
    ...(sellarDistributionEnabled
      ? [{ value: "sellarDistribution" as const, label: "Sellar Distribution" }]
      : []),
    { value: "direct", label: "Direct" },
    { value: "courier", label: "Courier" },
    { value: "haulier", label: "Haulier" },
    { value: "collection", label: "Collection" },
  ];

  const [previousActive, setPreviousActive] = useState(false);
  if (active !== previousActive) {
    setPreviousActive(active);
    if (active) {
      setIsDefault(false);
      setSelectedFulfillmentTypes(new Set());
      setSelectedPriceListId(null);
      setError("");
    }
  }

  const [previousPriceListId, setPreviousPriceListId] =
    useState(selectedPriceListId);
  if (selectedPriceListId !== previousPriceListId) {
    setPreviousPriceListId(selectedPriceListId);
    setIsDefault(false);
    setSelectedFulfillmentTypes(new Set());
  }

  const toggleFulfillmentType = (value: NonNullable<FulfillmentType>) => {
    setSelectedFulfillmentTypes((prev) => {
      const next = new Set(prev);
      if (next.has(value)) {
        next.delete(value);
      } else {
        next.add(value);
      }
      return next;
    });
  };

  const onSubmit = async () => {
    setError("");

    if (!selectedPriceListId) {
      setError("Please select a price list");
      return;
    }

    if (!isBasicMode && !isDefault && selectedFulfillmentTypes.size === 0) {
      setError("Please select at least one delivery method");
      return;
    }

    try {
      if (isBasicMode || isDefault) {
        await assignMutation.mutateAsync({
          PriceTierId: selectedPriceListId,
          fulfillmentType: null,
        });
      } else {
        await Promise.all(
          [...selectedFulfillmentTypes].map((type) =>
            assignMutation.mutateAsync({
              PriceTierId: selectedPriceListId!,
              fulfillmentType: type,
            }),
          ),
        );
      }
      setActive(false);
      setSelectedPriceListId(null);
      setSelectedFulfillmentTypes(new Set());
      setIsDefault(false);
    } catch (err) {
      const message =
        apiV2AssertError(err)?.response?.data.message ??
        "Failed to assign price list";
      setError(message);
    }
  };

  const renderAssignments = (
    existingAssignments: PriceListVolumeDiscountsListResponseType = [],
  ) => {
    const existingFulfillmentTypes = new Set(
      existingAssignments.map((a) => a.fulfillmentType),
    );
    const availableSpecificTypes = fulfillmentTypes.filter(
      (ft) => !existingFulfillmentTypes.has(ft.value),
    );

    const defaultAlreadyExists =
      existingAssignments?.some((a) => a.fulfillmentType === null) ?? false;

    const disabled =
      !selectedPriceListId ||
      (isBasicMode && (existingAssignments?.length ?? 0) > 0) ||
      (!isBasicMode && !isDefault && selectedFulfillmentTypes.size === 0);

    return (
      <>
        <DialogNavigation
          leftIcon="CHEVRON_DOWN"
          onClickLeftIcon={() => setActive(false)}
          title="Add a set of volume discounts"
          description={
            isBasicMode
              ? "You can apply one volume discount set to each price list. It will apply to every delivery method."
              : "You can apply your volume discount sets to more than one price list, and set under which delivery conditions they apply each time"
          }
        />
        <DialogContent className="flex flex-col gap-4">
          {priceListListQuery.isLoading ? (
            <SpinnerLegacy />
          ) : (
            <>
              <div className="flex flex-col gap-2">
                <InputLabel>Select price list</InputLabel>
                <InputContainer>
                  <InputSelect
                    value={selectedPriceListId?.toString() ?? ""}
                    onChange={(e) =>
                      setSelectedPriceListId(
                        e.target.value ? Number(e.target.value) : null,
                      )
                    }
                  >
                    <InputOption value="">Select a price list...</InputOption>
                    {priceLists.map((pt) => (
                      <InputOption key={pt.id} value={pt.id.toString()}>
                        {pt.name}
                      </InputOption>
                    ))}
                  </InputSelect>
                </InputContainer>
              </div>

              {isBasicMode &&
                selectedPriceListId &&
                (existingAssignments?.length ?? 0) > 0 && (
                  <p className="text-sm text-zinc-500">
                    This price list already has a volume discount set. Remove it
                    or set up conditional volume discounts to assign a different
                    set by delivery method.
                  </p>
                )}
              {selectedPriceListId && !isBasicMode && (
                <div className="flex flex-col gap-2">
                  <InputLabel>Select delivery conditions</InputLabel>
                  <p className="text-zinc-500">
                    You can choose to only have this volume discount set apply
                    when the order is being fulfilled by certain delivery
                    methods. Select which delivery methods below
                  </p>
                  <button
                    type="button"
                    onClick={() =>
                      !defaultAlreadyExists && setIsDefault((v) => !v)
                    }
                    className={`flex w-full items-center gap-2 rounded-lg border p-3 text-left text-sm transition-colors ${
                      defaultAlreadyExists
                        ? "cursor-not-allowed border-zinc-100 bg-zinc-50 text-zinc-400"
                        : isDefault
                          ? "border-zinc-400 bg-zinc-200 text-zinc-900"
                          : "border-zinc-200 text-zinc-700 hover:border-zinc-300"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isDefault}
                      disabled={defaultAlreadyExists}
                      readOnly
                      className="h-4 w-4 rounded border-zinc-300 accent-zinc-600"
                    />
                    Default (all delivery methods)
                    {defaultAlreadyExists && (
                      <span className="ml-auto text-xs">Already assigned</span>
                    )}
                  </button>
                  {availableSpecificTypes.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {availableSpecificTypes.map((ft) => {
                        const isSelected = selectedFulfillmentTypes.has(
                          ft.value,
                        );
                        return (
                          <button
                            key={ft.value}
                            type="button"
                            onClick={() =>
                              !isDefault && toggleFulfillmentType(ft.value)
                            }
                            className={`flex w-[calc(50%-4px)] items-center gap-2 rounded-lg border p-3 text-left text-sm transition-colors ${
                              isDefault
                                ? "cursor-not-allowed border-zinc-100 bg-zinc-50 text-zinc-400"
                                : isSelected
                                  ? "border-zinc-400 bg-zinc-200 text-zinc-900"
                                  : "border-zinc-200 text-zinc-700 hover:border-zinc-300"
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              disabled={isDefault}
                              onChange={() => toggleFulfillmentType(ft.value)}
                              onClick={(e) => e.stopPropagation()}
                              className="h-4 w-4 rounded border-zinc-300 accent-zinc-600"
                            />
                            {capitalize(ft.label)}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {availableSpecificTypes.length === 0 &&
                    defaultAlreadyExists && (
                      <p className="text-sm text-zinc-500">
                        All delivery method slots are already assigned for this
                        price list
                      </p>
                    )}
                </div>
              )}
              {isBasicMode && (
                <ConditionalVolumeDiscountsPrompt
                  onClick={() => {
                    setActive(false);
                    onSetUpConditionalVolumeDiscounts();
                  }}
                />
              )}
            </>
          )}
          {error && <p className="text-sm text-red-600">{error}</p>}
        </DialogContent>
        <DialogCTA>
          <Button
            onClick={onSubmit}
            disabled={disabled}
            loading={assignMutation.isPending}
          >
            Add
          </Button>
        </DialogCTA>
      </>
    );
  };

  return (
    <Dialog active={active} onDismiss={() => setActive(false)}>
      {selectedPriceListId === null ? (
        renderAssignments()
      ) : (
        <PriceListAssignments priceListId={selectedPriceListId}>
          {renderAssignments}
        </PriceListAssignments>
      )}
    </Dialog>
  );
};

const PriceListAssignments = ({
  priceListId,
  children,
}: {
  priceListId: number;
  children: (
    assignments?: PriceListVolumeDiscountsListResponseType,
  ) => React.ReactNode;
}) => {
  const { data } = usePriceListVolumeDiscountsList(priceListId);
  return children(data);
};
